import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/whole-life-insurance-quotes-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Types ──────────────────────────────────────────────────────────────── */
type WLAge = '25' | '30' | '35' | '40' | '45' | '50' | '55' | '60';
type WLCoverage = '25k' | '50k' | '100k' | '250k' | '500k';
type WLHealth = 'preferred' | 'standard' | 'substandard';

interface WLQuoteResult {
  carrier: string;
  amBest: string;
  monthlyPremium: number;
  annualPremium: number;
  cashValue10yr: number;
  cashValue20yr: number;
  deathBenefit: number;
  dividends: boolean;
  notes: string;
  badge?: string;
}

/* ─── Rate Engine ────────────────────────────────────────────────────────── */
const BASE_WL_RATES: Record<WLAge, Record<WLCoverage, number>> = {
  '25': { '25k': 22, '50k': 38, '100k': 68, '250k': 158, '500k': 298 },
  '30': { '25k': 26, '50k': 46, '100k': 84, '250k': 196, '500k': 374 },
  '35': { '25k': 32, '50k': 58, '100k': 108, '250k': 254, '500k': 490 },
  '40': { '25k': 42, '50k': 78, '100k': 148, '250k': 350, '500k': 682 },
  '45': { '25k': 56, '50k': 106, '100k': 204, '250k': 484, '500k': 946 },
  '50': { '25k': 76, '50k': 146, '100k': 284, '250k': 676, '500k': 1324 },
  '55': { '25k': 104, '50k': 202, '100k': 396, '250k': 944, '500k': 1850 },
  '60': { '25k': 144, '50k': 282, '100k': 556, '250k': 1326, '500k': 2600 },
};

const COVERAGE_AMOUNTS: Record<WLCoverage, number> = {
  '25k': 25000, '50k': 50000, '100k': 100000, '250k': 250000, '500k': 500000,
};

const HEALTH_MULT: Record<WLHealth, number> = {
  preferred: 1.0,
  standard: 1.22,
  substandard: 1.55,
};

function getWLQuotes(age: WLAge, coverage: WLCoverage, health: WLHealth): WLQuoteResult[] {
  const base = BASE_WL_RATES[age][coverage];
  const mult = HEALTH_MULT[health];
  const faceAmount = COVERAGE_AMOUNTS[coverage];
  const monthly = Math.round(base * mult);
  const annual = monthly * 12;

  // Cash value projections (approximate — whole life builds ~40% of premiums paid by yr 10, ~80% by yr 20)
  const cv10 = Math.round(annual * 10 * 0.38);
  const cv20 = Math.round(annual * 20 * 0.72);

  return [
    {
      carrier: 'Northwestern Mutual',
      amBest: 'A++',
      monthlyPremium: monthly,
      annualPremium: annual,
      cashValue10yr: cv10,
      cashValue20yr: cv20,
      deathBenefit: faceAmount,
      dividends: true,
      notes: 'Highest-rated carrier in the US — strong dividend history since 1872. Best for long-term wealth building.',
      badge: 'Top Pick',
    },
    {
      carrier: 'MassMutual',
      amBest: 'A++',
      monthlyPremium: Math.round(monthly * 0.97),
      annualPremium: Math.round(monthly * 0.97) * 12,
      cashValue10yr: Math.round(cv10 * 1.04),
      cashValue20yr: Math.round(cv20 * 1.06),
      deathBenefit: faceAmount,
      dividends: true,
      notes: 'Excellent dividend-paying whole life — strong cash value accumulation. Great for Indianapolis families.',
    },
    {
      carrier: 'New York Life',
      amBest: 'A++',
      monthlyPremium: Math.round(monthly * 1.02),
      annualPremium: Math.round(monthly * 1.02) * 12,
      cashValue10yr: Math.round(cv10 * 0.98),
      cashValue20yr: Math.round(cv20 * 1.02),
      deathBenefit: faceAmount,
      dividends: true,
      notes: 'America\'s largest mutual life insurer — consistent dividends, strong financial stability.',
    },
    {
      carrier: 'Guardian Life',
      amBest: 'A++',
      monthlyPremium: Math.round(monthly * 0.95),
      annualPremium: Math.round(monthly * 0.95) * 12,
      cashValue10yr: Math.round(cv10 * 1.02),
      cashValue20yr: Math.round(cv20 * 1.04),
      deathBenefit: faceAmount,
      dividends: true,
      notes: 'Competitive whole life rates — strong dividend history. Good option for Indianapolis business owners.',
    },
    {
      carrier: 'Mutual of Omaha',
      amBest: 'A+',
      monthlyPremium: Math.round(monthly * 0.88),
      annualPremium: Math.round(monthly * 0.88) * 12,
      cashValue10yr: Math.round(cv10 * 0.88),
      cashValue20yr: Math.round(cv20 * 0.90),
      deathBenefit: faceAmount,
      dividends: false,
      notes: 'Most affordable whole life option — non-participating (no dividends) but lower premiums. Good for budget-conscious buyers.',
    },
  ];
}

/* ─── FAQs ───────────────────────────────────────────────────────────────── */
const FAQS = [
  {
    q: 'How much does whole life insurance cost in Indianapolis?',
    a: 'Whole life insurance costs in Indianapolis depend on your age, coverage amount, and health class. Here are sample monthly rates for a $100,000 whole life policy: Age 25: $68–$84/month. Age 30: $84–$104/month. Age 35: $108–$134/month. Age 40: $148–$184/month. Age 45: $204–$252/month. Age 50: $284–$352/month. Age 55: $396–$490/month. Age 60: $556–$688/month. Whole life insurance costs significantly more than term life insurance for the same death benefit — but it builds cash value, lasts your entire life, and premiums never increase. An independent agent compares all available carriers to find your best whole life rate in Indianapolis.',
  },
  {
    q: 'What is the difference between whole life and term life insurance in Indianapolis?',
    a: 'The key differences between whole life and term life insurance in Indianapolis: Term life insurance: Lower monthly premiums. Coverage for a set period (10–30 years). No cash value. Expires at end of term. Best for income replacement and mortgage protection. Whole life insurance: Higher monthly premiums. Coverage for your entire life — never expires. Builds cash value you can borrow against. Premiums are locked in and never increase. Can pay dividends (with participating policies). Best for permanent protection, estate planning, and wealth building. For most Indianapolis families with a mortgage and young children, term life insurance is the better value. Whole life makes more sense for estate planning, business succession, or as a wealth-building tool. An independent agent can help you determine which is right for your situation.',
  },
  {
    q: 'What is cash value in whole life insurance and how does it work in Indianapolis?',
    a: 'Cash value is a savings component built into whole life insurance policies. Here\'s how it works for Indianapolis policyholders: A portion of each premium payment goes into a cash value account. The cash value grows tax-deferred at a guaranteed rate (typically 2–4%). With participating policies (Northwestern Mutual, MassMutual, New York Life, Guardian), dividends can accelerate cash value growth. You can borrow against your cash value at any time — no credit check, no approval required. Policy loans are not taxable income. If you surrender the policy, you receive the cash value minus any surrender charges. Cash value grows slowly in the early years — typically 30–40% of premiums paid by year 10, 60–80% by year 20. For Indianapolis residents using whole life as a wealth-building tool, the cash value component is the key advantage over term life insurance.',
  },
  {
    q: 'What are dividends in whole life insurance and do Indianapolis policies pay them?',
    a: 'Dividends in whole life insurance are a return of excess premium paid to policyholders of participating (mutual) insurance companies. Here\'s what Indianapolis whole life policyholders need to know: Which carriers pay dividends: Northwestern Mutual, MassMutual, New York Life, and Guardian Life are the major dividend-paying carriers available in Indianapolis. All four have paid dividends every year for over 100 years. How dividends work: Dividends are not guaranteed — they depend on the carrier\'s investment performance, mortality experience, and expenses. However, the major mutual carriers have extremely consistent dividend histories. What you can do with dividends: (1) Take as cash. (2) Apply to reduce your premium. (3) Buy additional paid-up insurance (increases death benefit and cash value). (4) Leave on deposit to earn interest. Dividend-paying whole life policies typically outperform non-participating policies over 20+ years, despite higher initial premiums.',
  },
  {
    q: 'Is whole life insurance a good investment for Indianapolis residents?',
    a: 'Whether whole life insurance is a good investment for Indianapolis residents depends on your financial goals. Whole life insurance makes sense if: You need permanent life insurance coverage (estate planning, business succession, final expenses). You\'ve maxed out other tax-advantaged accounts (401k, IRA, HSA). You want a guaranteed, tax-deferred savings component. You\'re a business owner using life insurance for key person coverage or buy-sell agreements. You want to build generational wealth for your Indianapolis family. Whole life insurance may not be the best choice if: You primarily need income replacement for your family (term life is more cost-effective). You haven\'t maxed out your 401k and IRA contributions. You need maximum coverage for minimum premium. The bottom line: whole life insurance is not a replacement for a diversified investment portfolio — but it can be a valuable component of a comprehensive financial plan. An independent agent can help you determine if whole life fits your Indianapolis financial goals.',
  },
  {
    q: 'Can I get whole life insurance quotes online in Indianapolis?',
    a: 'Yes — you can get whole life insurance quotes online in Indianapolis, but there are important limitations to be aware of. Online quote tools: Most online quote tools only show term life insurance rates. Whole life insurance quotes require more information and are more complex to generate accurately. Our Whole Life Quote Calculator above provides estimated rates for Indianapolis residents based on age, coverage, and health class. For accurate whole life quotes: The most accurate whole life insurance quotes in Indianapolis come from working directly with an independent agent who can compare all available carriers simultaneously. Whole life underwriting considers more factors than term life — health history, family history, occupation, and financial justification for the coverage amount. An independent agent can also help you compare participating vs. non-participating policies, dividend options, and riders that can significantly affect the value of your whole life policy.',
  },
  {
    q: 'What whole life insurance riders are available in Indianapolis?',
    a: 'Whole life insurance riders in Indianapolis can significantly enhance your policy\'s value. Common riders available: Paid-Up Additions (PUA) Rider: Allows you to purchase additional paid-up insurance with extra premium payments — accelerates cash value growth significantly. Waiver of Premium Rider: Waives your premium if you become disabled — your policy stays in force without payment. Guaranteed Insurability Rider: Allows you to purchase additional coverage at specific ages without a medical exam — valuable if your health changes. Accelerated Death Benefit Rider: Allows you to access a portion of your death benefit if diagnosed with a terminal illness. Long-Term Care Rider: Allows you to use your death benefit for long-term care expenses. Term Rider: Adds temporary term coverage to your whole life policy — increases death benefit at lower cost. The Paid-Up Additions rider is particularly valuable for Indianapolis residents using whole life as a wealth-building tool — it dramatically accelerates cash value growth in the early years.',
  },
  {
    q: 'How do I compare whole life insurance quotes in Indianapolis?',
    a: 'Comparing whole life insurance quotes in Indianapolis requires looking beyond just the monthly premium. Key factors to compare: Monthly premium: The amount you pay each month — but this is just the starting point. Cash value projections: How much cash value will you have at years 10, 20, and 30? Dividend history: For participating policies, what is the carrier\'s dividend history? Guaranteed vs. illustrated values: Carriers show both guaranteed values (minimum) and illustrated values (based on current dividend scale). Compare guaranteed values for an apples-to-apples comparison. AM Best rating: Financial strength of the carrier — stick with A+ or A++ rated carriers. Riders available: What optional riders are available and at what cost? Surrender charges: Are there surrender charges if you cancel the policy early? The most important step: work with an independent agent who can compare all available carriers simultaneously and show you side-by-side illustrations. Call Hoosier Life Insurance at (317) 648-2983 for a free whole life quote comparison.',
  },
  {
    q: 'What is the best whole life insurance company in Indianapolis?',
    a: 'The best whole life insurance company in Indianapolis depends on your specific needs and goals. For wealth building and maximum cash value: Northwestern Mutual and MassMutual consistently rank highest for long-term cash value accumulation and dividend performance. For most affordable premiums: Mutual of Omaha offers the most competitive whole life premiums — non-participating (no dividends) but lower monthly cost. For business owners: Guardian Life is particularly strong for business life insurance applications in Indianapolis. For estate planning: New York Life and Northwestern Mutual are the top choices for large estate planning policies. For seniors and final expense: Mutual of Omaha and Foresters Financial offer competitive whole life options for older Indianapolis residents. The key: there is no single "best" whole life insurance company for all Indianapolis residents. The best carrier depends on your age, health, coverage needs, and financial goals. An independent agent compares all available carriers to find your best fit.',
  },
  {
    q: 'How much whole life insurance do I need in Indianapolis?',
    a: 'The right amount of whole life insurance for Indianapolis residents depends on your purpose for buying it. For income replacement: 10–12x your annual income is the standard recommendation. For a $75,000 income, that\'s $750,000–$900,000 in coverage. For final expenses: $15,000–$25,000 covers most funeral and burial costs in Indianapolis. For estate planning: Coverage amount depends on your estate size and goals — typically $250,000–$1M+. For business succession: Coverage equals the value of your business interest or key person\'s economic value. For wealth building: Coverage amount depends on how much premium you can afford — the Paid-Up Additions rider allows you to maximize cash value growth. The most important step: work with an independent agent who can analyze your specific situation and recommend the right coverage amount for your Indianapolis financial goals.',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  {
    name: 'David & Karen M.',
    location: 'Carmel, Indianapolis',
    quote: 'We wanted whole life for estate planning. Benjamin compared 5 carriers and found us Northwestern Mutual at a rate we could actually afford. The cash value projections were eye-opening — we had no idea how much it would grow.',
    stars: 5,
    coverage: '$500K whole life',
    goal: 'Estate Planning',
  },
  {
    name: 'Thomas R.',
    location: 'Fishers, Indianapolis',
    quote: 'Business owner — needed whole life for a buy-sell agreement. Benjamin found Guardian Life, which was perfect for our situation. The policy is funded and our partnership is protected.',
    stars: 5,
    coverage: '$1M whole life',
    goal: 'Business Buy-Sell',
  },
  {
    name: 'Margaret & Bill S.',
    location: 'Noblesville, Indianapolis',
    quote: 'We wanted to leave something for our grandchildren. Benjamin set us up with MassMutual whole life — the dividend projections are incredible. We\'re building generational wealth for our Indianapolis family.',
    stars: 5,
    coverage: '$250K each',
    goal: 'Generational Wealth',
  },
  {
    name: 'Jennifer L.',
    location: 'Broad Ripple, Indianapolis',
    quote: 'Single mom, wanted permanent coverage for my kids. Benjamin found me a $100K whole life policy for $108/month. The cash value will be there if I ever need it. Peace of mind is priceless.',
    stars: 5,
    coverage: '$100K whole life',
    goal: 'Permanent Protection',
  },
];

/* ─── Carrier Comparison ─────────────────────────────────────────────────── */
const CARRIERS = [
  { name: 'Northwestern Mutual', amBest: 'A++', dividends: true, cashValue: 'Excellent', premiums: 'Higher', best: 'Wealth building & estate planning', icon: 'ri-shield-star-line' },
  { name: 'MassMutual', amBest: 'A++', dividends: true, cashValue: 'Excellent', premiums: 'Higher', best: 'Long-term cash value accumulation', icon: 'ri-shield-check-line' },
  { name: 'New York Life', amBest: 'A++', dividends: true, cashValue: 'Very Good', premiums: 'Higher', best: 'Estate planning & large policies', icon: 'ri-building-2-line' },
  { name: 'Guardian Life', amBest: 'A++', dividends: true, cashValue: 'Very Good', premiums: 'Moderate', best: 'Business owners & buy-sell agreements', icon: 'ri-briefcase-line' },
  { name: 'Mutual of Omaha', amBest: 'A+', dividends: false, cashValue: 'Good', premiums: 'Most Affordable', best: 'Budget-conscious buyers & seniors', icon: 'ri-home-heart-line' },
];

/* ─── Component ─────────────────────────────────────────────────────────── */
export default function WholeLifeInsuranceQuotesIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const [wlAge, setWlAge] = useState<WLAge>('40');
  const [wlCoverage, setWlCoverage] = useState<WLCoverage>('100k');
  const [wlHealth, setWlHealth] = useState<WLHealth>('standard');
  const [quotes, setQuotes] = useState<WLQuoteResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Whole Life Insurance Quotes Indianapolis IN | Cash Value Calculator | Hoosier Life Insurance';
    const description = 'Get whole life insurance quotes in Indianapolis, IN. Compare cash value projections from top carriers. Use our Whole Life Quote Calculator. Call (317) 648-2983.';
    const keywords = 'whole life insurance quotes Indianapolis, whole life insurance Indianapolis Indiana, cash value life insurance Indianapolis, permanent life insurance quotes Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/9d27aed9eac93757497a8e6655df486e.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Whole Life Insurance Quotes Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: WLAge[] = ['25', '30', '35', '40', '45', '50', '55', '60'];
  const coverageOptions: { value: WLCoverage; label: string }[] = [
    { value: '25k', label: '$25K' },
    { value: '50k', label: '$50K' },
    { value: '100k', label: '$100K' },
    { value: '250k', label: '$250K' },
    { value: '500k', label: '$500K' },
  ];
  const healthOptions: { value: WLHealth; label: string; desc: string }[] = [
    { value: 'preferred', label: 'Preferred', desc: 'Excellent health, no major conditions' },
    { value: 'standard', label: 'Standard', desc: 'Average health, minor conditions' },
    { value: 'substandard', label: 'Substandard', desc: 'Some health conditions' },
  ];

  const bestQuote = quotes ? quotes[0] : null;

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/whole-life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Whole Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Whole Life Quotes</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20professional%20couple%20in%20their%2040s%20sitting%20at%20modern%20home%20office%20desk%20reviewing%20whole%20life%20insurance%20documents%20cash%20value%20wealth%20building%20concept%20warm%20contemporary%20home%20environment%20Marion%20County%20Indiana%20natural%20light%20relaxed%20confident%20financial%20planning%20family%20protection%20photorealistic%20warm%20neutral%20tones%20beige%20cream%20background&width=1440&height=700&seq=wl-quotes-indy-hero-2025a&orientation=landscape"
            alt="Whole Life Insurance Quotes Indianapolis Indiana — Cash Value Calculator"
            title="Whole Life Insurance Quotes in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-safe-line text-sm"></i>
              Indianapolis &amp; Marion County · Whole Life Insurance · Cash Value · 5 Top Carriers
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Whole Life Insurance Quotes —{' '}
              <span className="text-[#D97642]">Compare Permanent Coverage in Indianapolis</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Compare whole life insurance quotes from the top carriers in Indianapolis. Use our <strong className="text-white">Whole Life Quote Calculator</strong> to see premiums, cash value projections, and dividend potential side by side.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '5', label: 'Top whole life carriers compared' },
                { value: 'A++', label: 'Highest-rated carriers available' },
                { value: 'Tax-Free', label: 'Cash value grows tax-deferred' },
                { value: 'Lifetime', label: 'Coverage that never expires' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#quote-calculator" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-calculator-line text-lg"></i>
                Get My Whole Life Quote
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '100+', label: 'Years of dividends paid by top carriers — Northwestern Mutual, MassMutual, NYL' },
              { value: '~72%', label: 'Of premiums returned as cash value by year 20 with participating policies' },
              { value: 'A++', label: 'Highest AM Best rating — available from 4 of the 5 carriers we compare' },
              { value: 'Free', label: 'Whole life quote comparison — no obligation, no pressure' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Whole Life Insurance Quotes in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Whole life insurance premiums are locked in at purchase — they never increase, regardless of age or health changes',
                  'Cash value grows tax-deferred and can be borrowed against at any time — no credit check, no approval required',
                  'Participating policies (Northwestern Mutual, MassMutual, NYL, Guardian) pay dividends that can accelerate cash value growth',
                  'Whole life insurance lasts your entire life — unlike term life, it never expires as long as premiums are paid',
                  'The Paid-Up Additions rider dramatically accelerates cash value growth — ideal for wealth-building strategies',
                  'An independent agent compares all carriers simultaneously — some are 10–20% cheaper than others for the same coverage',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT */}
          <div>

            {/* QUOTE CALCULATOR */}
            <div id="quote-calculator" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Whole Life Quote Calculator</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Whole Life Insurance Quotes &amp; Cash Value — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, and health class to see estimated whole life premiums and cash value projections from the top 5 carriers in Indianapolis.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-calculator-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  {/* Age */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setWlAge(a); setQuotes(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${wlAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setWlCoverage(opt.value); setQuotes(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${wlCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Health */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Health Class</label>
                    <div className="grid grid-cols-3 gap-3">
                      {healthOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setWlHealth(opt.value); setQuotes(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${wlHealth === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${wlHealth === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setQuotes(getWLQuotes(wlAge, wlCoverage, wlHealth))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-calculator-line text-lg"></i>
                    Calculate My Whole Life Quote
                  </button>
                </div>

                {/* Results */}
                {quotes !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Whole Life Quotes — ${COVERAGE_AMOUNTS[wlCoverage].toLocaleString()} · Age {wlAge} · {wlHealth.charAt(0).toUpperCase() + wlHealth.slice(1)} Health
                      </h3>
                      {bestQuote && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                          From ${Math.min(...quotes.map(q => q.monthlyPremium))}/mo
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-4 mb-6">
                      {quotes.map((q, i) => (
                        <div key={q.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{q.carrier}</span>
                                  {q.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{q.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {q.amBest}</span>
                                  {q.dividends && <span className="text-xs bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">Pays Dividends</span>}
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{q.notes}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${q.monthlyPremium}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${q.annualPremium.toLocaleString()}/yr</div>
                            </div>
                          </div>
                          {/* Cash Value Row */}
                          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200">
                            <div className="text-center">
                              <div className="text-xs text-gray-400 mb-0.5">Death Benefit</div>
                              <div className="text-sm font-bold text-[#1A3A52]">${q.deathBenefit.toLocaleString()}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-gray-400 mb-0.5">Cash Value Yr 10</div>
                              <div className="text-sm font-bold text-[#D97642]">${q.cashValue10yr.toLocaleString()}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-gray-400 mb-0.5">Cash Value Yr 20</div>
                              <div className="text-sm font-bold text-[#4A7C59]">${q.cashValue20yr.toLocaleString()}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-[#1A3A52]/5 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates and cash value projections are estimates for illustration purposes. Actual values depend on full underwriting, dividend performance, and carrier selection. Cash value projections assume current dividend scale for participating policies. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized whole life quote.
                      </p>
                    </div>

                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Whole Life Quote — Free 15-Min Call
                    </button>
                  </div>
                )}

                {quotes === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-safe-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage, and health class above, then click <strong className="text-[#1A3A52]">Calculate My Whole Life Quote</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Carrier Comparison */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Carrier Comparison</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Best Whole Life Insurance Carriers in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Not all whole life carriers are equal. Here's how the top 5 carriers available in Indianapolis compare on the factors that matter most.
              </p>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4 grid grid-cols-5 gap-2 text-xs font-semibold text-white/70 uppercase tracking-wide">
                  <div className="col-span-2">Carrier</div>
                  <div className="text-center">Dividends</div>
                  <div className="text-center">Cash Value</div>
                  <div className="text-center">Premiums</div>
                </div>
                <div className="divide-y divide-gray-100">
                  {CARRIERS.map((c, i) => (
                    <div key={i} className={`px-6 py-4 grid grid-cols-5 gap-2 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}>
                      <div className="col-span-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                            <i className={`${c.icon} text-sm text-[#D97642]`}></i>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-[#1A3A52]">{c.name}</div>
                            <div className="text-xs text-gray-400">AM Best {c.amBest}</div>
                          </div>
                        </div>
                      </div>
                      <div className="text-center">
                        {c.dividends
                          ? <span className="text-xs font-bold text-[#4A7C59] bg-[#4A7C59]/10 px-2 py-0.5 rounded-full whitespace-nowrap">Yes</span>
                          : <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">No</span>}
                      </div>
                      <div className="text-center">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${c.cashValue === 'Excellent' ? 'bg-[#4A7C59]/10 text-[#4A7C59]' : c.cashValue === 'Very Good' ? 'bg-[#D97642]/10 text-[#D97642]' : 'bg-gray-100 text-gray-500'}`}>
                          {c.cashValue}
                        </span>
                      </div>
                      <div className="text-center">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${c.premiums === 'Most Affordable' ? 'bg-[#4A7C59]/10 text-[#4A7C59]' : c.premiums === 'Moderate' ? 'bg-[#D97642]/10 text-[#D97642]' : 'bg-red-50 text-red-400'}`}>
                          {c.premiums}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Families Who Chose Whole Life Insurance
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#1A3A52]/10 text-[#1A3A52] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.goal}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Whole Life Insurance Quotes Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-wl-quotes-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-safe-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Whole Life Quote</h3>
                    <p className="text-xs text-white/70">5 top carriers compared · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Cash value projections</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Dividend comparison</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No obligation</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm">
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · All ages welcome</p>
              </div>
            </div>

            {/* Sample Rates */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                Sample Whole Life Rates — $100K Coverage
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { age: 'Age 30', rate: '$84/mo', cv20: '$14,500' },
                  { age: 'Age 35', rate: '$108/mo', cv20: '$18,700' },
                  { age: 'Age 40', rate: '$148/mo', cv20: '$25,600' },
                  { age: 'Age 45', rate: '$204/mo', cv20: '$35,300' },
                  { age: 'Age 50', rate: '$284/mo', cv20: '$49,100' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-xs font-semibold text-[#1A3A52]">{row.age}</span>
                    <div className="flex gap-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Monthly</div>
                        <div className="text-xs font-bold text-[#1A3A52]">{row.rate}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Cash Value Yr 20</div>
                        <div className="text-xs font-bold text-[#4A7C59]">{row.cv20}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Standard health class. Rates vary by carrier and health profile.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Whole Life Call</h3>
                    <p className="text-xs text-white/70">All carriers compared · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Whole Life Quote Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Coverage goal or questions? (Optional)',
                    submitButton: 'Confirm My Free Whole Life Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/whole-life-insurance-indianapolis', label: 'Whole Life Insurance Indianapolis', icon: 'ri-safe-line' },
                  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/final-expense-insurance-indianapolis', label: 'Final Expense Insurance Indianapolis', icon: 'ri-heart-line' },
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers', icon: 'ri-search-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to see your whole life insurance quote in Indianapolis?
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Whole Life Quote Calculator above, then call for your exact personalized quote with full cash value illustrations. 5 A++ rated carriers compared. No obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#quote-calculator" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-calculator-line text-lg"></i>
              Calculate My Quote
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Whole Life Insurance Quotes Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Whole Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Get Your Whole Life Insurance Quote — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
